/**
 * Marquee section: clone the slide track enough times to fill the viewport,
 * then start a seamless CSS animation that scrolls exactly one track width.
 *
 * Architecture:
 *   - Liquid renders one `.section-marquee__track` (accessible, visible to screen readers).
 *   - This script measures the track width, then appends as many aria-hidden clones as
 *     needed so the total content is always wider than the visible viewport.
 *   - `--marquee-offset` is set to `-trackWidth px` so the CSS keyframe scrolls by
 *     exactly one track, creating a seamless loop regardless of clone count.
 *   - `is-ready` is added after clones exist, so animation only starts once the DOM
 *     is correct (avoids a visible jump on init).
 *   - Re-init on `shopify:section:load` is safe: all existing clones are removed first.
 */
(function () {
    'use strict';

    /**
     * @param {HTMLElement} wrapper - The `[data-marquee-wrapper]` element.
     */
    function initMarqueeWrapper(wrapper) {
        const track = wrapper.querySelector('[data-marquee-track]');
        if (!track) return;

        wrapper.classList.remove('is-ready');
        wrapper.style.removeProperty('--marquee-offset');
        wrapper.querySelectorAll('[data-marquee-clone]').forEach(function (el) {
            el.remove();
        });

        const trackWidth = track.scrollWidth;
        if (trackWidth === 0) return;

        const viewportWidth = document.documentElement.clientWidth;
        const clonesNeeded = Math.max(1, Math.ceil(viewportWidth / trackWidth));

        for (let i = 0; i < clonesNeeded; i++) {
            const clone = document.createElement('div');
            clone.className = track.className;
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-marquee-clone', '');
            clone.innerHTML = track.innerHTML;
            clone.querySelectorAll('a, button, [tabindex], input, select, textarea').forEach(function(el) {
                el.setAttribute('tabindex', '-1');
            });
            wrapper.appendChild(clone);
        }

        wrapper.style.setProperty('--marquee-offset', '-' + trackWidth + 'px');

        requestAnimationFrame(function () {
            wrapper.classList.add('is-ready');
        });
    }

    /**
     * @param {Element|Document} [root] - Search scope; defaults to document.
     */
    function initAll(root) {
        const scope = root || document;
        scope.querySelectorAll('[data-marquee-wrapper]').forEach(initMarqueeWrapper);
    }

    document.addEventListener('DOMContentLoaded', function () {
        initAll();
    });

    document.addEventListener('shopify:section:load', function (event) {
        initAll(event.target);
    });

    let resizeTimer = null;
    window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(initAll, 200);
    });
})();
